import _ from "lodash";
import type {
	AnalysisType,
	GroundDataAssessment,
	GrowthCurveOption,
	SummaryTablePreset,
	TimeSeriesPreset,
	TrialAnalysisDataOptions,
} from "../../../../apis/types/trialTypes";
import type {
	AnalysisAssessmentData,
	AnalysisSelection,
	BaseAssessmentData,
	GroundDataAssessmentData,
	GroundDataSelection,
	GrowthCurveSelection,
	SummaryTableSelection,
} from "./TrialAnalysisContext";

export const getOrderedTimeSeriesPresets = (trialOptions: TrialAnalysisDataOptions) => {
	const trialAnalyses = _.chain(trialOptions.trialAnalysisOptions)
		.flatMap<AnalysisType>("analysisTypes")
		.uniqBy("id")
		.value();

	const ordered = _.chain(trialOptions.presetOptions.timeSeriesPresets)
		.reduce((acc, tsp) => {
			const analysis = _.find(
				trialAnalyses,
				(ta) =>
					ta.id === tsp.analysisId &&
					ta.analysisTypeId === tsp.analysisTypeId &&
					(ta.quantifiedRegionTypeId === tsp.quantifiedRegionTypeId ||
						ta.quantifiedRegionTypeId === tsp.secondaryQuantifiedRegionTypeId),
			);
			if (analysis) {
				acc.push({ ...tsp, instanceCount: analysis.instanceCount });
			}
			return acc;
		}, [] as TimeSeriesPreset[])
		.orderBy(["order", "name"])
		.value();

	return ordered;
};

export const getGroundDataAssessments = (trialOptions: TrialAnalysisDataOptions) => {
	const categoryMap = _.keyBy(trialOptions.presetOptions.groundDataPresets, "groundDataAssessmentId");

	const groundAssessments = _.chain(trialOptions.groundDataOptions)
		.flatMap<GroundDataAssessment>("groundDataAssessments")
		.uniqBy("id")
		.map((gda) => ({ ...gda, assessmentCategoryId: categoryMap[gda.id].assessmentCategoryId }))
		.value();

	return groundAssessments;
};

export const getGrowthCurveAssessments = (trialOptions: TrialAnalysisDataOptions) => {
	const growthParametersId = _.find(trialOptions.presetOptions.categories, { name: "Growth Parameters" })?.id;
	const presetMap = _.keyBy(
		trialOptions.presetOptions.growthCurvePresets,
		(gcp) => `${gcp.analysisId}_${gcp.curveModelAnalysisId}`,
	);

	const growthAssessments = _.chain(trialOptions.growthCurveOptions)
		.map((gco) => {
			const preset = presetMap[`${gco.analysisId}_${gco.curveModelAnalysisId}`];
			return {
				...gco,
				id: gco.curveModelName,
				name: preset?.name ?? gco.curveModelName,
				assessmentCategoryId: preset?.assessmentCategoryId ?? growthParametersId,
				order: preset?.order ?? Infinity,
			};
		})
		.orderBy(["order", "name"])
		.value();

	return growthAssessments;
};

/**
 * Builds the flat list of date entries (flights + ground data collections) used as the
 * base checkbox list in SummaryTableSelections before a specific analysis type is chosen.
 */
export const buildBaseAssessmentData = (trialOptions: TrialAnalysisDataOptions): BaseAssessmentData[] => {
	const flightDates: BaseAssessmentData[] = trialOptions.trialAnalysisOptions.map((tao) => ({
		id: tao.flightId,
		date: tao.flightDate,
		enabled: true,
		checked: true,
	}));

	const groundDates: BaseAssessmentData[] = trialOptions.groundDataOptions.map((gdo) => ({
		id: gdo.groundDatasetId,
		date: gdo.assessmentDate,
		enabled: true,
		checked: true,
	}));

	return _.orderBy([...flightDates, ...groundDates], "date");
};

/**
 * Builds the per-flight checkbox list for an analysis (flight) selection row.
 * A flight is enabled if that flight has the given analysisTypeId + qrTypeId combination.
 * A flight is checked if that combination is marked checked on the server.
 */
export const buildFlightAssessments = (
	trialOptions: TrialAnalysisDataOptions,
	analysisTypeId: string,
	quantifiedRegionTypeId: string,
	name: string,
): AnalysisAssessmentData[] => {
	return trialOptions.trialAnalysisOptions.map((tao) => ({
		id: tao.flightId,
		date: tao.flightDate,
		enabled: _.some(tao.analysisTypes, { id: analysisTypeId, quantifiedRegionTypeId }),
		checked: _.some(tao.analysisTypes, { id: analysisTypeId, quantifiedRegionTypeId, checked: true }),
		type: "analysis",
		groundDataAssessmentId: "",
		assessmentType: analysisTypeId,
		name,
		displayName: name,
		quantifiedRegionTypeId,
	}));
};

/**
 * Builds the per-ground-dataset checkbox list for a ground data selection row.
 * A dataset is enabled if it contains the given groundDataAssessmentId.
 * Defaults checked=true whenever enabled (matching the original fallback behaviour).
 */
export const buildGroundDataAssessments = (
	trialOptions: TrialAnalysisDataOptions,
	groundDataAssessmentId: string,
	name: string,
): GroundDataAssessmentData[] => {
	return trialOptions.groundDataOptions.map((gdo) => {
		const enabled = _.some(gdo.groundDataAssessments, { id: groundDataAssessmentId });
		return {
			id: gdo.groundDatasetId,
			date: gdo.assessmentDate,
			enabled,
			checked: enabled, // original code defaulted checked=true when enabled && !serverChecked
			type: "grounddata",
			groundDataAssessmentId,
			assessmentType: groundDataAssessmentId,
			name,
			displayName: name,
		};
	});
};

/**
 * Derives the summary table selection rows from a summary table preset.
 *
 * Priority:
 *  1. Saved selections — use assessment types where selected=true && enabled=true (persisted by the user).
 *  2. Preset — if nothing is saved and a preset is provided, match its sections against available types.
 *  3. Empty — if neither applies, returns [].
 *
 * Stand Count rows are always moved to the end to match legacy ordering.
 */
export const buildSummaryTableSelectionsFromPreset = (
	trialOptions: TrialAnalysisDataOptions,
	summaryTablePreset?: SummaryTablePreset,
	initialize?: boolean,
): SummaryTableSelection[] => {
	const allAnalysisTypes = trialOptions.trialAnalysisOptions.flatMap((tao) => tao.analysisTypes);
	const allGroundDataAssessments = trialOptions.groundDataOptions.flatMap((gdo) => gdo.groundDataAssessments);

	let hasSaved = false;
	let selectedGrowthCurves: GrowthCurveOption[] = [];
	let selectedAnalysisTypes: AnalysisType[] = [];
	let selectedGroundData: GroundDataAssessment[] = [];

	// --- 1. Saved selections ---
	if (initialize) {
		const savedGrowthCurves = trialOptions.growthCurveOptions.filter((gco) => gco.selected && gco.enabled);
		const savedAnalysisTypes = _.uniqWith(
			allAnalysisTypes.filter((at) => at.selected && at.enabled),
			(a, b) => a.id === b.id && a.quantifiedRegionTypeId === b.quantifiedRegionTypeId,
		);
		const savedGroundData = _.uniqBy(
			allGroundDataAssessments.filter((gda) => gda.selected && gda.enabled),
			"id",
		);

		hasSaved = savedGrowthCurves.length > 0 || savedAnalysisTypes.length > 0 || savedGroundData.length > 0;

		selectedGrowthCurves = savedGrowthCurves;
		selectedAnalysisTypes = savedAnalysisTypes;
		selectedGroundData = savedGroundData;
	}

	// --- 2. Preset fallback ---
	if (!hasSaved && summaryTablePreset) {
		const presetGrowthCurves: typeof trialOptions.growthCurveOptions = [];
		const presetAnalysisTypes: typeof allAnalysisTypes = [];
		const presetGroundData: typeof allGroundDataAssessments = [];

		for (const section of summaryTablePreset.sections) {
			for (const pa of section.presetAnalyses) {
				presetAnalysisTypes.push(
					...allAnalysisTypes.filter(
						// Some analysis types carry an undocumented `analysisId` from the server; fall back
						// to matching by `id` (which is how Stand Count works in the original preset logic).
						// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
						(at) => (at as any).analysisId === pa.analysisId || at.id === pa.analysisId,
					),
				);
			}

			for (const pcm of section.presetCurveModels) {
				presetGrowthCurves.push(
					...trialOptions.growthCurveOptions.filter(
						(gco) =>
							gco.curveModelAnalysisId === pcm.curveModelAnalysisId &&
							(!pcm.analysisId || gco.analysisId === pcm.analysisId) &&
							(!pcm.analysisTypeId || gco.analysisTypeId === pcm.analysisTypeId) &&
							(!pcm.quantifiedRegionTypeId || gco.quantifiedRegionTypeId === pcm.quantifiedRegionTypeId),
					),
				);
			}

			for (const pgd of section.presetGroundData) {
				presetGroundData.push(...allGroundDataAssessments.filter((gda) => gda.id === pgd.groundDataAssessmentId));
			}
		}

		selectedGrowthCurves = presetGrowthCurves;
		selectedAnalysisTypes = _.uniqWith(
			presetAnalysisTypes,
			(a, b) => a.id === b.id && a.quantifiedRegionTypeId === b.quantifiedRegionTypeId,
		);
		selectedGroundData = _.uniqBy(presetGroundData, "id");
	}

	// --- 3. Build rows ---
	const growthCurveRows: GrowthCurveSelection[] = selectedGrowthCurves.map((gco) => {
		const matchingDisplayName = _.find(
			trialOptions.presetOptions.curveModelAnalysisDisplayNames,
			(dn) =>
				dn.analysisId === gco.analysisId &&
				dn.analysisTypeId === gco.analysisTypeId &&
				dn.primaryCurveModelAnalysisId === gco.curveModelAnalysisId &&
				(dn.primaryQuantifiedRegionTypeId === gco.quantifiedRegionTypeId ||
					dn.secondaryQuantifiedRegionTypeId === gco.quantifiedRegionTypeId),
		);

		return {
			id: gco.curveModelName,
			name: gco.curveModelName,
			displayName: matchingDisplayName?.name ?? gco.curveModelName,
			isAnalysis: false,
			isGrowthCurveAnalysis: true,
			isCurveModel: true,
			analysisId: gco.analysisId,
			curveModelAnalysisId: gco.curveModelAnalysisId,
			analysisTypeId: gco.analysisTypeId,
			analysisValue: null,
			assessments: null,
			startDate: gco.curveModelAnalysisStartDate,
			endDate: gco.curveModelAnalysisEndDate,
			checked: true,
			enabled: true,
			selected: gco.selected,
			initialLoad: true,
		};
	});

	const analysisRows: AnalysisSelection[] = selectedAnalysisTypes.map((at) => ({
		id: at.id,
		name: at.name,
		displayName: at.name,
		isAnalysis: true,
		isGrowthCurveAnalysis: false,
		isCurveModel: false,
		analysisValue: at.id,
		// Stand Count uses at.id as the analysisId in the API call (historical server quirk)
		analysisId: at.name.includes("Stand Count (LAI") ? at.id : undefined,
		analysisTypeId: at.analysisTypeId,
		curveModelAnalysisId: null,
		quantifiedRegionTypeOptionId: at.quantifiedRegionTypeId,
		assessments: buildFlightAssessments(trialOptions, at.id, at.quantifiedRegionTypeId, at.name),
		initialLoad: true,
	}));

	const groundDataRows: GroundDataSelection[] = selectedGroundData.map((gda) => ({
		id: gda.id,
		name: gda.name,
		displayName: gda.name,
		isAnalysis: false,
		isGrowthCurveAnalysis: false,
		isCurveModel: false,
		analysisValue: gda.id,
		curveModelAnalysisId: null,
		assessments: buildGroundDataAssessments(trialOptions, gda.id, gda.name),
		initialLoad: true,
	}));

	const selections: SummaryTableSelection[] = [...growthCurveRows, ...analysisRows, ...groundDataRows];

	// Stand Count must always be last
	const standCountIdx = selections.findIndex((s) => "displayName" in s && s.displayName?.includes("Stand Count (LAI"));
	if (standCountIdx !== -1) {
		selections.push(selections.splice(standCountIdx, 1)[0]);
	}

	return selections;
};

/**
 * Resolves the `isPlotUniformity` request flag from the currently selected analysis type.
 *
 * Uniformity is not a separate analysis — it shares an `analysisId` with its Aerial
 * counterpart and is distinguished only by this flag, so it is derived on demand from
 * `analysisTypeId` rather than stored alongside it.
 */
export const getIsPlotUniformity = (trialOptions: TrialAnalysisDataOptions, analysisTypeId: string | null) => {
	if (!analysisTypeId) return false;
	return _.find(trialOptions.presetOptions.analysisTypes, (at) => at.id === analysisTypeId)?.name === "Uniformity";
};
